/** Display helpers for the web chat visitor card (pure; unit-tested). */
import type { WebContext, WebEmailNotice, WebVisitorInfo } from '../api/types'

const DEVICE: Record<string, string> = { mobile: 'มือถือ', tablet: 'แท็บเล็ต', desktop: 'คอมพิวเตอร์' }

export const deviceLabel = (d: WebVisitorInfo['device']): string | null => (d ? (DEVICE[d] ?? null) : null)

/** "cleanfitdelivery.com/order/status" — host (no www.) + path, without the query. */
export function shortUrl(url: string | null): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    const path = u.pathname === '/' ? '' : u.pathname
    return `${u.hostname.replace(/^www\./, '')}${path}`
  } catch {
    return url
  }
}

/** Where the visitor came from, in words: campaign first, else the referring site, else direct. */
export function sourceText(v: Pick<WebVisitorInfo, 'utm' | 'referrer'>): string {
  const u = v.utm
  if (u && (u.source || u.campaign)) {
    return ['source', 'medium', 'campaign'].map((k) => u[k]).filter(Boolean).join(' · ')
  }
  const host = shortUrl(v.referrer)?.split('/')[0]
  return host ? `ลิงก์จาก ${host}` : 'เข้าเว็บโดยตรง'
}

/** Seen within this = still on the site. The server touches last_seen_at at most once a minute and uses the same 150 s
 *  (api/app/webchat/notices.py ONLINE_S, runtime.py SEEN_EVERY_S): 90 s made the card flicker "ออกจากหน้าเว็บ 2 นาที"
 *  between two touches of a visitor still on the page. */
export const ONLINE_S = 150

/** "เปิดหน้าเว็บอยู่" when the widget was seen within ONLINE_S, else "ออกจากหน้าเว็บ 12 นาทีที่แล้ว". */
export function presenceText(lastSeenIso: string, nowMs: number): { online: boolean; text: string } {
  const s = Math.max(0, Math.round((nowMs - Date.parse(lastSeenIso)) / 1000))
  if (s < ONLINE_S) return { online: true, text: 'เปิดหน้าเว็บอยู่' }
  const m = Math.round(s / 60)
  if (m < 60) return { online: false, text: `ออกจากหน้าเว็บ ${m} นาทีที่แล้ว` }
  const h = Math.round(m / 60)
  if (h < 24) return { online: false, text: `ออกจากหน้าเว็บ ${h} ชั่วโมงที่แล้ว` }
  return { online: false, text: `ออกจากหน้าเว็บ ${Math.round(h / 24)} วันที่แล้ว` }
}

/** "14:32" in Bangkok time (+ "พรุ่งนี้" / a date when not today) — for the e-mail notice line. */
function bkkWhen(iso: string, nowMs: number): string {
  const t = new Date(Date.parse(iso) + 7 * 3600_000)
  const today = new Date(nowMs + 7 * 3600_000)
  const hm = `${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')}`
  const day = (d: Date) => `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`
  if (day(t) === day(today)) return hm
  if (day(t) === day(new Date(nowMs + 31 * 3600_000))) return `พรุ่งนี้ ${hm}`
  const p = posDate(`${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`, nowMs)
  return `${p ?? ''} ${hm}`.trim()
}

/** The e-mail notice of a web chat (api/app/webchat/notices.py): แจ้งทางอีเมลแล้ว 14:32 · รอส่ง 08:30 · ไม่ได้ส่ง: … */
export function emailNoticeText(n: WebEmailNotice | null | undefined, nowMs: number): { text: string; tone: 'ok' | 'wait' | 'off' } | null {
  if (!n) return null
  if (n.state === 'sent') return { text: `แจ้งทางอีเมลแล้ว${n.at ? ` ${bkkWhen(n.at, nowMs)}` : ''}`, tone: 'ok' }
  if (n.state === 'queued') {
    const when = n.at ? ` ${bkkWhen(n.at, nowMs)}` : ''
    return { text: `รอส่ง${when}${n.reason_text ? ` · ${n.reason_text}` : ''}`, tone: 'wait' }
  }
  return { text: `ไม่ได้ส่ง: ${n.reason_text ?? 'ไม่ทราบสาเหตุ'}`, tone: 'off' }
}

// ---------------------------------------------------------------- website context (cart, messages, trail) + POS orders

export const STEP_LABEL: Record<string, string> = {
  pack: 'เลือกแพ็ก',
  menu: 'เลือกเมนู',
  checkout: 'ที่อยู่และชำระ',
  payment: 'ชำระเงิน',
  bill: 'ดูคำสั่งซื้อ',
  account: 'หน้าบัญชี',
  other: 'หน้าอื่นบนเว็บ',
}

export const MODE_LABEL: Record<string, string> = { continue: 'สั่งต่อแพ็กเดิม', group: 'สั่งด้วยกัน' }

export const KIND_LABEL: Record<string, string> = {
  pay_failed: 'ชำระเงินไม่สำเร็จ',
  pay_expired: 'หมดเวลาชำระ',
  area: 'พื้นที่จัดส่ง',
  ship_quote: 'ค่าส่งและวันส่ง',
  date: 'วันส่ง',
  cutoff: 'เลยเวลาสั่ง',
  coupon: 'โค้ดส่วนลด',
  form: 'ข้อมูลไม่ครบ',
  login: 'เข้าสู่ระบบ',
  stock: 'เมนูปิดขาย',
  notice: 'เว็บแจ้ง',
  error: 'เว็บขัดข้อง',
}

const PAY_METHOD: Record<string, string> = { promptpay: 'QR พร้อมเพย์', creditcard: 'บัตรเครดิต', transfer: 'โอนเงิน' }
export const payMethodLabel = (m: string | null): string | null => (m ? (PAY_METHOD[m] ?? m) : null)

/** "เมื่อสักครู่" · "12 นาทีที่แล้ว" · "3 ชั่วโมงที่แล้ว" · "เมื่อวาน" · "4 วันที่แล้ว". */
export function relTime(iso: string | null, nowMs: number): string {
  if (!iso) return ''
  const s = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 1000))
  if (s < 60) return 'เมื่อสักครู่'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} นาทีที่แล้ว`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} ชั่วโมงที่แล้ว`
  const d = Math.floor(h / 24)
  return d === 1 ? 'เมื่อวาน' : `${d} วันที่แล้ว`
}

/** How long the visitor has been on a step: "ไม่ถึง 1 นาที" · "12 นาที" · "1 ชม. 5 นาที" · "2 วัน". */
export function spanText(sinceIso: string | null, nowMs: number): string | null {
  if (!sinceIso) return null
  const m = Math.floor(Math.max(0, nowMs - Date.parse(sinceIso)) / 60000)
  if (m < 1) return 'ไม่ถึง 1 นาที'
  if (m < 60) return `${m} นาที`
  const h = Math.floor(m / 60)
  if (h < 24) return m % 60 ? `${h} ชม. ${m % 60} นาที` : `${h} ชม.`
  return `${Math.floor(h / 24)} วัน`
}

/** Campaign of this visit in words: "แอด Facebook · sep-7day" · "facebook · cpc · sep" · "ลิงก์จาก line.me". */
export function adText(ad: WebContext['ad']): string | null {
  if (!ad) return null
  const u = ad.utm ?? {}
  const campaign = u.campaign ?? null
  if (ad.click) return [`แอด ${ad.click === 'facebook' ? 'Facebook' : 'Google'}`, campaign].filter(Boolean).join(' · ')
  if (u.source || u.campaign) return ['source', 'medium', 'campaign'].map((k) => u[k]).filter(Boolean).join(' · ')
  const host = shortUrl(ad.referrer)?.split('/')[0]
  return host ? `ลิงก์จาก ${host}` : null
}

export const baht = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `฿${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`

const TH_MONTH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const TH_WDAY = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']

/** POS date "2026-09-29" (+ " 22:00:00") → "อ. 29 ก.ย." (+ " 22:00"); another year adds the Thai year. */
export function posDate(value: string | null, nowMs: number, withTime = false): string | null {
  const m = value ? /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value) : null
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const wd = new Date(Date.UTC(y, mo - 1, d)).getUTCDay()
  const thisYear = new Date(nowMs + 7 * 3600_000).getUTCFullYear() === y
  let out = `${TH_WDAY[wd]} ${d} ${TH_MONTH[mo - 1]}${thisYear ? '' : ` ${String(y + 543).slice(-2)}`}`
  if (withTime && m[4]) out += ` ${m[4]}:${m[5]}`
  return out
}

/** "ก.พ. 68" — month + short Thai year (customer since …). */
export function monthYear(value: string | null): string | null {
  const m = value ? /^(\d{4})-(\d{2})/.exec(value) : null
  return m ? `${TH_MONTH[Number(m[2]) - 1]} ${String(Number(m[1]) + 543).slice(-2)}` : null
}
